import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const InvoiceUploadGuide = () => {
  const [isApiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [isTaxRateModalOpen, setTaxRateModalOpen] = useState(false);

  return (
    <>
      <SEO 
        title="請求書CSVアップロードの使い方｜Billwave利用ガイド"
        description="Stripe連携による請求書CSVアップロードの一括発行手順を解説。APIキー設定、税率登録、CSVファイルの作成、予約発行(issue_at)、エラー時の再送信方法までわかりやすくご案内します。"
        path="/support/how-to-use/invoice-upload"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={[
          { label: '使い方', url: '/support/how-to-use' },
          { label: '請求書CSVアップロード' }
        ]} />

        <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
            使い方 - 請求書CSVアップロード
          </h1>
          <p className="text-lg text-gray-800 mb-6 leading-relaxed">
            請求書CSVアップロードではCSVファイルを読み込ませることでStripeから請求書を一括発行・予約発行することができます。
          </p>

          {/* Section 1: Preparation */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. 利用の準備</h2>
            <p className="mb-4 text-gray-700">
              本機能の利用には<a href="https://stripe.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe</a>のアカウントが必要です。<br />
              ※Stripeのアカウントをお持ちでない方はアカウントを作成してください。
            </p>
            <p className="mb-4 text-gray-700">
              アカウントをお持ちの方はStripeのダッシュボードにログインし、以下の情報を取得してください。
            </p>
            <ul className="list-disc list-inside mb-4 pl-4 bg-gray-50 p-4 rounded-md">
              <li className="text-gray-700">
                <button 
                  type="button"
                  className="text-blue-600 hover:underline cursor-pointer font-medium" 
                  onClick={() => setApiKeyModalOpen(true)}
                >
                  Stripe APIキー
                </button> (<code className="bg-gray-200 px-2 py-1 rounded">rk_...</code> もしくは <code className="bg-gray-200 px-2 py-1 rounded">sk_...</code> から始まるキー)
              </li>
              <li className="text-gray-700 mt-2">
                <button 
                  type="button"
                  className="text-blue-600 hover:underline cursor-pointer font-medium" 
                  onClick={() => setTaxRateModalOpen(true)}
                >
                  税率 ID
                </button>
              </li>
            </ul>
            <p className="mb-4 text-gray-700">
              メニューの「設定」→「Billwave設定」を選択してください。<br />
              先程確認した「Stripe APIキー」と「税率 ID」を設定・保存してください。<br />
              税率IDは複数登録できます。商品によって税込み・税別がある場合、それぞれの税率IDを登録してください。
            </p>
            <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-800 p-4 rounded-r-md" role="alert">
              <p className="font-bold">ヒント</p>
              <p className="mt-2">インボイス制度に対応した請求書を発行するには適格請求書発行事業者登録番号をStripeに事前設定する必要があります。</p>
              <p className="mt-2">Stripeのダッシュボードより設定を行ってください。<br />「設定」→「ビリング」→「請求書の税務情報」にある「納税者番号」に番号を登録してください。</p>
            </div>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 2: CSV Creation */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. CSV作成方法</h2>
            <p className="mb-4 text-gray-700">
              CSVファイルのフォーマットをダウンロードしてください。<br />
              CSVファイルのフォーマットに請求書を発行するための情報を入力します。<br />
              CSVファイルの項目と入力値については「サポート」の<Link to="/support/csv-format" className="text-blue-600 hover:underline">「CSVフォーマット」</Link>を確認してください。
            </p>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 3: CSV Upload */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. CSVアップロード方法</h2>
            <p className="mb-4 text-gray-700">
              作成したCSVファイルを選択、もしくはドラッグアンドドロップします。<br />
              準備ができたら「アップロード」ボタンを押してください。<br />
              エラーが出た場合、CSVファイルに入力した値に不備があります。<br />
              エラー内容をもとに入力した値を修正してください。
            </p>
          </section>
          
          <hr className="my-8 border-gray-200" />

          {/* Section 4: Billing Flow */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. 請求書発行の流れ</h2>
            <p className="mb-4 text-gray-700">
              CSVファイルのアップロードが完了するとStripeから請求が開始されます。<br />
              日時指定をしている場合は「予約済み請求書」対象となる請求書や請求日時を確認できます。
            </p>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 5: Checking Invoices */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. 発行された請求書の確認方法</h2>
            <p className="mb-4 text-gray-700">
              アップロードされた請求書は処理されると「請求履歴」に表示されます。<br />
              請求日時、Stripeの顧客ID、請求金額、Stripeによる請求書発行の結果、エラーが発生した場合のエラーメッセージをここで確認できます。<br />
              また、発行された請求書はStripeのダッシュボード「請求書」でご確認いただけます。
            </p>
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-r-md" role="alert">
              <p className="font-bold">注意</p>
              <p>「結果」欄は請求書発行の、成功、失敗を表示しています。請求結果ではありません。実際の請求結果はStripeでご確認ください。</p>
            </div>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 6: Scheduled Issuance */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. 発行予約 (<code className="bg-gray-200 px-2 py-1 rounded">issue_at</code>) の使い方</h2>
            <p className="mb-4 text-gray-700">
              CSVファイルの<code className="bg-gray-200 px-2 py-1 rounded">issue_at</code>の項目で日時を指定すると指定時刻から請求書の発行が開始されます（指定時刻は日本時間となります）。<br />
              請求書は順次処理されるため、請求書の数によっては指定時刻以降の発行となる場合があります。
            </p>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 7: Resending Invoices */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. 請求書の再送方法</h2>
            <p className="mb-4 text-gray-700">
              請求書の発行でエラーが発生した場合、「請求履歴」エラー内容を確認し、ファイルを修正してください。<br />
              修正後、エラーが発生している請求書だけのファイルを作成、再度アップロードしてください。
            </p>
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-r-md" role="alert">
              <p className="font-bold">注意</p>
              <p>「結果」が成功となっている請求書はStripeから請求書が発行（もしくは下書き保存）されています。再度アップロードすると2重で請求書が発行されるのでご注意ください。</p>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link 
              to="/support/how-to-use"
              className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-bold py-2.5 px-6 rounded-lg transition-colors"
            >
              「使い方」一覧へ戻る
            </Link>
            <Link
              to="/signup"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition-colors shadow"
            >
              無料で試してみる
            </Link>
          </div>

          {/* API Key Modal */}
          <Modal 
            isOpen={isApiKeyModalOpen} 
            onClose={() => setApiKeyModalOpen(false)} 
            title="Stripe APIキーの作成方法"
          >
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Stripeにログインし左下の「開発者」をクリック「APIキー」に遷移します。</li>
              <li>「＋シークレットキーを作成」ボタンを押してください。</li>
              <li>作成するAPIキーに「名前」にわかりやすい名前をつけてください（例：Billwave用）URLには「https://billwave.jp/」と入力してください。</li>
              <li>「このキーに対する権限をカスタマイズする」はチェックなしです（※個別に権限設定をした方はチェックを入れて進めてください）</li>
              <li>「このキーを別のWEBサイトに提供」を選択「制限付きのキーを作成する」をクリック</li>
              <li>APIキーが生成されます</li>
            </ol>
          </Modal>

          {/* Tax Rate ID Modal */}
          <Modal 
            isOpen={isTaxRateModalOpen} 
            onClose={() => setTaxRateModalOpen(false)} 
            title="税率IDの確認方法"
          >
            <p className="text-gray-700">税率IDは以下で確認できます。</p>
            <ol className="list-decimal list-inside space-y-2 mt-2 text-gray-700">
              <li>「商品カタログ」を選択しメニューにある「税率」を選択してください。</li>
              <li>「txr_******」と言うIDが税率IDになります。</li>
            </ol>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default InvoiceUploadGuide;
